import { useState } from 'react'
import { Controller, FormContext, useForm } from 'react-hook-form'
import {
  Box,
  FormControlLabel,
  FormHelperText,
  Grid,
  Radio,
  RadioGroup,
  TextField,
} from '@material-ui/core'
import { DatePicker } from '@material-ui/pickers'
import { Alert } from '@material-ui/lab'
import moment from 'moment'

import { Card, ConfirmationDialog, Label, Select } from 'components'
import { BenefitsSection } from './components'

import helpers from 'helpers'
import * as service from 'service'
import PropTypes from 'prop-types'

import { useFetch, useSnackbar } from 'hooks'

import theme from 'theme'
import schema from './schema'
import useStyles from './styles'
import constants from 'constants/index'

const ResultStep = ({ churn, setLoading, handleNextStep, show }) => {
  const [hasBenefits, setHasBenefits] = useState(
    churn?.discount || churn?.gratuity ? true : false,
  )
  const [pendingData, setPendingData] = useState(null)
  const [openFidelityDialog, setOpenFidelityDialog] = useState(false)

  const classes = useStyles()
  const snackbar = useSnackbar()

  const { response: paymentContractServicesResponse } = useFetch(
    service.dponet.paymentContractServices.get,
    {
      paymentContractId: churn?.paymentContract?.id,
      perPage: 1000,
      name: 'Plataforma',
    },
    [churn?.paymentContract?.id],
    !!churn?.paymentContract?.id,
  )

  const platformService = (
    paymentContractServicesResponse?.data?.paymentServices || []
  ).find((paymentService) => paymentService?.name === 'Plataforma')

  const benefits = churn?.discount ? 'discount' : 'freeMonthlyFee'

  const formMethods = useForm({
    validationSchema: schema({
      hasBenefits,
      platformServicePrice: platformService?.price,
    }),
    defaultValues: {
      reason: churn?.reason || '',
      status: churn?.status || '',
      description: churn?.description || '',
      gratuity: churn?.gratuity || '',
      discount: churn?.discount
        ? helpers.formatters.currencyInput(churn.discount)
        : '',
      accessCancellationDate: churn?.accessCancellationDate
        ? moment(churn.accessCancellationDate).format('YYYY-MM-DD')
        : null,
      contractFinalizationDate: churn?.contractFinalizationDate
        ? moment(churn.contractFinalizationDate).format('YYYY-MM-DD')
        : moment().add(31, 'days').format('YYYY-MM-DD'),
      benefits: benefits,
    },
  })

  const { control, errors, handleSubmit, watch } = formMethods

  const { response: churnReasonsResponse, isLoading: isLoadingChurnReasons } =
    useFetch(service.dponet.churnReasons.get, { perPage: 1000 }, [])

  const churnReasonItems = (churnReasonsResponse?.data?.churnReasons || []).map(
    (churnReason) => ({ id: churnReason.name, name: churnReason.name }),
  )

  const status = watch('status')

  const isChurn = status === 'churn'
  const isConverted = status === 'converted'

  const onSubmit = async (data) => {
    if (
      data?.status === 'churn' &&
      churn?.paymentContract?.fidelityAlertMessage
    ) {
      setPendingData(data)
      setOpenFidelityDialog(true)
      return
    }

    await changeContract(data)
  }

  const handleConfirmFidelity = async () => {
    setOpenFidelityDialog(false)
    await changeContract(pendingData)
  }

  const changeContract = async (data) => {
    setLoading(true)

    try {
      const isDiscount = hasBenefits && data?.benefits === 'discount'

      await service.dponet.churns.changeContract({
        churnId: churn.id,
        churn: {
          status: data?.status,
          reason: data?.reason,
          description: data?.description,
          gratuity: data?.gratuity,
          discount: isDiscount
            ? helpers.formatters.currencyOutput(data?.discount)
            : undefined,
          accessCancellationDate: data?.accessCancellationDate
            ? moment(data.accessCancellationDate).format('YYYY-MM-DD')
            : null,
          contractFinalizationDate: data?.contractFinalizationDate
            ? moment(data.contractFinalizationDate).format('YYYY-MM-DD')
            : null,
        },
      })

      snackbar.open({
        message: `Resultado atualizado com sucesso!`,
        variant: 'success',
      })
      handleNextStep()
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error?.response?.data?.error),
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <FormContext {...formMethods}>
      <Box
        component="form"
        id={constants.churn.CHURN_STEPPER_RESULT_ID_FORM}
        onSubmit={handleSubmit(onSubmit)}
        className={classes.container}
      >
        <Card title="Informações após a reunião" paddingCard={0}>
          <Box className={classes.container}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  as={
                    <RadioGroup>
                      <Label title="Resultado da reunião" />
                      <Box display="flex" gridGap={theme.spacing(2)}>
                        <FormControlLabel
                          label="Convertido"
                          value="converted"
                          control={<Radio color="primary" />}
                          disabled={show}
                        />
                        <FormControlLabel
                          label="Não convertido"
                          value="churn"
                          control={<Radio color="primary" />}
                          disabled={show}
                        />
                      </Box>
                    </RadioGroup>
                  }
                  control={control}
                  name="status"
                />
                {!!errors?.status && (
                  <FormHelperText error={!!errors?.status}>
                    {errors?.status?.message}
                  </FormHelperText>
                )}
              </Grid>
              {isChurn && (
                <Grid item xs={12}>
                  <Controller
                    as={
                      <Select
                        items={churnReasonItems}
                        loading={isLoadingChurnReasons}
                        label="Motivo"
                        disabled={show}
                        error={!!errors?.reason}
                        helperText={errors?.reason?.message}
                      />
                    }
                    control={control}
                    name="reason"
                    mode="onChange"
                  />
                </Grid>
              )}
              {(isChurn || isConverted) && (
                <Grid item xs={12}>
                  <Controller
                    as={
                      <TextField
                        type="text"
                        color="primary"
                        variant="outlined"
                        label="Descrição do motivo (Opcional)"
                        error={!!errors?.description}
                        helperText={errors?.description?.message}
                        fullWidth
                        disabled={show}
                        multiline
                        minRows={5}
                        maxRows={5}
                      />
                    }
                    control={control}
                    name="description"
                  />
                </Grid>
              )}
              {isChurn && (
                <>
                  <Grid item xs={12}>
                    <Alert severity="info">
                      O contrato só será cancelado se o churn for do tipo
                      Completo. O acesso será removido somente das empresas
                      presentes na aba Empresas.
                    </Alert>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      as={
                        <DatePicker
                          label="Data de cancelamento do contrato"
                          format="DD/MM/yyyy"
                          error={!!errors?.contractFinalizationDate}
                          helperText={errors?.contractFinalizationDate?.message}
                          onChange={(newValue) => ({ value: newValue })}
                          minDate={moment().add(1, 'day')}
                          fullWidth
                          disabled={show}
                        />
                      }
                      control={control}
                      name="contractFinalizationDate"
                      mode="onChange"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      as={
                        <DatePicker
                          label="Data de cancelamento de acesso das empresas"
                          format="DD/MM/yyyy"
                          error={!!errors?.accessCancellationDate}
                          helperText={errors?.accessCancellationDate?.message}
                          onChange={(newValue) => ({ value: newValue })}
                          minDate={moment().add(1, 'day')}
                          fullWidth
                          disabled={show}
                        />
                      }
                      control={control}
                      name="accessCancellationDate"
                      mode="onChange"
                    />
                  </Grid>
                </>
              )}
            </Grid>
            {isConverted && (
              <BenefitsSection
                hasBenefits={hasBenefits}
                setHasBenefits={setHasBenefits}
                show={show}
                platformServicePrice={platformService?.price}
                contractPrice={churn?.paymentContract?.price}
              />
            )}
          </Box>
        </Card>
      </Box>
      {openFidelityDialog && (
        <ConfirmationDialog
          open={openFidelityDialog}
          setOpen={setOpenFidelityDialog}
          message="Atenção: contrato com fidelidade ativa"
          alert={churn?.paymentContract?.fidelityAlertMessage}
          alertColor="warning"
          buttonText="Estou ciente, prosseguir"
          cancelButtonText="Cancelar"
          actionAcceptButton={handleConfirmFidelity}
        />
      )}
    </FormContext>
  )
}

ResultStep.propTypes = {
  churn: PropTypes.object,
  setLoading: PropTypes.func,
  handleNextStep: PropTypes.func,
  show: PropTypes.bool,
}

export default ResultStep
